import { getApolloClient } from '@/lib/apollo-client'
import { getMainDefinition } from '@apollo/client/utilities'
import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import type { GraphQLError, GraphQLResponse } from '@nhost/nhost-js/graphql'
import type { FetchResponse } from '@nhost/nhost-js/fetch'
import type { OperationDefinitionNode } from 'graphql'
import { nhost } from '@/lib/nhost'
import { captureGraphQLErrors } from '@/lib/monitoring/graphql-error-capture'
import { isBusinessValidationOnly } from '@/lib/business-validation-errors'
import {
  consumeUnauthenticatedFailure,
  shouldSkipUnauthenticatedCapture,
} from '@/lib/monitoring/unauthenticated-capture-guard'

export interface NormalizedGraphQLError {
  message: string
  code?: string
  path?: string[]
  locations?: { line: number; column: number }[]
  status?: number
  extensions?: Record<string, unknown>
}

export class GraphQLRequestError<TData = unknown> extends Error {
  public readonly errors: NormalizedGraphQLError[]
  public readonly status: number
  public readonly response: FetchResponse<GraphQLResponse<TData>>

  constructor(
    message: string,
    options: {
      errors: NormalizedGraphQLError[]
      status: number
      response: FetchResponse<GraphQLResponse<TData>>
      cause?: unknown
    }
  ) {
    super(message, { cause: options.cause })
    this.name = 'GraphQLRequestError'
    this.errors = options.errors
    this.status = options.status
    this.response = options.response
  }
}

export type GraphQLVariablesLike = Record<string, unknown> | Record<string, never>

export interface GqlRequestOptions {
  /**
   * Suppress automatic error toast notification.
   * Use this when you want to handle errors yourself (e.g., show custom formatted messages).
   */
  suppressToast?: boolean
}

/**
 * Execute a GraphQL request using Apollo Client
 * This makes all mutations and queries visible in Apollo DevTools
 *
 * Queries run with fetchPolicy 'no-cache' BY DESIGN: results reach the caller
 * but never land in Apollo's normalized cache, so no useQuery observer is
 * updated. Do NOT use gqlRequest for refresh-after-write flows — sibling
 * components will keep showing stale data until reload. For those, call
 * client.query({ fetchPolicy: 'network-only' }) directly (and remember: any
 * cache-writing policy requires `id` on EVERY nested selection, or Apollo v4
 * throws "Missing field 'id' while extracting keyFields").
 */
export async function gqlRequest<TData, TVars extends GraphQLVariablesLike = GraphQLVariablesLike>(
  document: TypedDocumentNode<TData, TVars>,
  variables?: TVars,
  options?: GqlRequestOptions
): Promise<FetchResponse<GraphQLResponse<TData>>> {
  const client = getApolloClient()

  // Determine operation type
  const definition = getMainDefinition(document)
  const operation = (definition as OperationDefinitionNode).operation

  // Subscriptions cannot be executed via HTTP - they require WebSocket
  if (operation === 'subscription') {
    throw new Error('gqlRequest does not support subscriptions. Use Apollo useSubscription hook instead.')
  }

  const isMutation = operation === 'mutation'

  // Auth guard (skip: !session at the transport chokepoint).
  // Service-layer queries run outside the React/ProtectedRoute lifecycle, so a
  // lingering effect — e.g. a dashboard metric refetch firing during sign-out or
  // a session-expiry redirect — would otherwise reach Hasura with no JWT. The
  // authLink omits the Authorization header when there is no session, so the
  // request runs as the anonymous role, which does not expose aggregate fields
  // like `reservations_aggregate`. That surfaces a spurious
  // "field not found in type 'query_root'" error toast on the login page.
  // An authenticated user always has a session object (even with an expired
  // token, which the authLink / JWT-retry path refreshes), so this only
  // short-circuits when the user is fully signed out. Mutations are user-driven
  // and left to surface genuine errors.
  if (!isMutation && typeof window !== 'undefined' && !nhost.getUserSession()) {
    return {
      body: { data: null as unknown as TData },
      status: 200,
      headers: new Headers(),
    }
  }

  try {
    let data: TData | null | undefined

    if (isMutation) {
      // Use Apollo's mutate for mutations - visible in DevTools
      const result = await client.mutate<TData>({
        mutation: document,
        variables: variables as Record<string, unknown>,
      })
      data = result.data
    } else {
      // Use Apollo's query for queries
      // Use no-cache for service layer queries to avoid cache interference
      // Service queries run outside React component lifecycle
      const result = await client.query<TData>({
        query: document,
        variables: variables as Record<string, unknown>,
        fetchPolicy: 'no-cache', // Avoid cache issues in service layer
        errorPolicy: 'none', // Ensure GraphQL errors throw instead of returning partial data
      })
      data = result.data
    }

    // Return in the same format as before for backwards compatibility
    return {
      body: { data: data as TData },
      status: 200,
      headers: new Headers(),
    }
  } catch (error) {
    // Handle Apollo errors
    const apolloError = error as { graphQLErrors?: GraphQLError[]; networkError?: Error; message?: string }
    const graphQLErrors = apolloError.graphQLErrors || []
    const normalizedErrors = normalizeGraphQLErrors(graphQLErrors, 400)

    const message =
      normalizedErrors.map(err => err.message).join('; ') ||
      apolloError.message ||
      'GraphQL request failed'

    // Check if this is a store reset error (not a real failure, query will retry)
    // Return empty response instead of throwing - the query was cancelled, not failed
    const isStoreResetError = message.includes('Store reset while query was in flight')
    if (isStoreResetError) {
      return {
        body: { data: null as unknown as TData },
        status: 200,
        headers: new Headers(),
      }
    }

    // JWT expiry no longer needs a refresh-and-retry here: the Apollo authLink refreshes the token
    // pre-emptively (nhost.refreshSession(60)) before every request, and a genuinely invalid token
    // is handled by the errorLink (sign out + redirect). We only detect it to suppress the toast —
    // the user is being redirected.
    const isJwtExpired = message.includes('JWTExpired') || message.includes('jwt expired')

    const response: FetchResponse<GraphQLResponse<TData>> = {
      body: { errors: graphQLErrors as unknown as GraphQLError[] },
      status: 400,
      headers: new Headers(),
    }

    const graphQLError = new GraphQLRequestError<TData>(message, {
      errors: normalizedErrors,
      status: 400,
      response,
      cause: error,
    })

    // Report to GlitchTip with operation + Hasura-code context. The Apollo
    // errorLink also sees hook-based operations, but service-layer calls with
    // errorPolicy 'none' surface here as the single choke point.
    //
    // Business-rule rejections are the one exception, and this path used to
    // lack the skip the errorLink already had — so a deposit-gate or
    // posted-night rejection routed through a service call still paged
    // (2026-08-04 triage). The toast below still fires; only capture is skipped.
    // ⛔ Same for requests that ran WITHOUT a JWT (role `public`). The Apollo
    // errorLink logs "Skipping capture of unauthenticated ..." and returns —
    // but that only suppresses ITS capture, not this one, so #2402 arrived in
    // GlitchTip carrying the skip message in its own breadcrumbs. The
    // transport's decision is read back here via the shared guard; the session
    // check is the fallback for a failure that never reached the errorLink.
    // The toast below is untouched: only the ERROR REPORT is suppressed.
    const operationName = (definition as OperationDefinitionNode).name?.value
    const ranUnauthenticated =
      consumeUnauthenticatedFailure(operationName) ||
      (typeof window !== 'undefined' && !nhost.getUserSession())
    const skipUnauthenticated = shouldSkipUnauthenticatedCapture({
      ranUnauthenticated,
      isAuthFailure: isJwtExpired,
    })

    const capturedErrors = normalizedErrors.length ? normalizedErrors : [{ message }]
    if (!isBusinessValidationOnly(capturedErrors) && !skipUnauthenticated) {
      captureGraphQLErrors({
        operationName,
        operationType: operation,
        errors: capturedErrors,
        source: 'gql-request',
        handled: isJwtExpired,
        variableKeys: variables ? Object.keys(variables) : [],
      })
    }

    // Show error toast unless suppressed (skip for JWT errors — user will be redirected)
    if (typeof window !== 'undefined' && !options?.suppressToast && !isJwtExpired) {
      void import('@/lib/notifications').then(mod => {
        if (typeof mod.showGraphQLErrorToast === 'function') {
          mod.showGraphQLErrorToast(graphQLError)
        }
      })
    }

    throw graphQLError
  }
}

export type GqlTransport = typeof gqlRequest

function normalizeGraphQLErrors(
  errors: GraphQLError[] | undefined,
  status: number
): NormalizedGraphQLError[] {
  if (!errors?.length) return []
  return errors.map(err => {
    const extensions =
      typeof err.extensions === 'object' && err.extensions !== null
        ? (err.extensions as Record<string, unknown>)
        : undefined
    return {
      message: err.message,
      code: typeof extensions?.code === 'string' ? extensions.code : undefined,
      path: err.path as string[] | undefined,
      locations: err.locations as { line: number; column: number }[] | undefined,
      status,
      extensions,
    }
  })
}
